"use client";

import { useCallback, useEffect, useState } from "react";

export type TouchControlsSettings = Readonly<{
  /** Whether the selected mobile control input is enabled. */
  visible: boolean;
  /** Mobile control style: direct board gestures or the on-screen joystick. */
  inputMode: TouchInputMode;
  /** 0 = opaque, 1 = fully transparent. Transparent buttons still receive input. */
  transparency: number;
  /** Whether the mobile landing-position guide is shown. */
  ghostVisible: boolean;
  /** Whether the mobile fast downward swipe is enabled. */
  hardDropEnabled: boolean;
  /** Backward-compatible alias for the portrait layout. */
  layout: TouchControlsLayout;
  /** Normalized positions used while the viewport is portrait. */
  portraitLayout: TouchControlsLayout;
  /** Normalized positions used while the viewport is landscape. */
  landscapeLayout: TouchControlsLayout;
}>;

export type TouchInputMode = "gesture" | "buttons" | "joystick";

export type TouchControlsOrientation = "portrait" | "landscape";

export type TouchControlName =
  | "left"
  | "softDropLeft"
  | "softDrop"
  | "softDropRight"
  | "hardDrop"
  | "right"
  | "ccw"
  | "cw";

export type TouchControlPosition = Readonly<{
  x: number;
  y: number;
}>;

export type TouchControlsLayout = Readonly<Record<TouchControlName, TouchControlPosition>>;

export const DEFAULT_TOUCH_CONTROLS_LAYOUT: TouchControlsLayout = {
  left: { x: 20, y: 82 },
  softDropLeft: { x: 35, y: 82 },
  softDrop: { x: 50, y: 82 },
  softDropRight: { x: 65, y: 82 },
  hardDrop: { x: 80, y: 82 },
  right: { x: 80, y: 82 },
  ccw: { x: 74, y: 73 },
  cw: { x: 90, y: 73 },
};

const LEGACY_DEFAULT_TOUCH_CONTROLS_LAYOUT: TouchControlsLayout = {
  left: { x: 20, y: 82 },
  softDropLeft: { x: 35, y: 82 },
  softDrop: { x: 50, y: 82 },
  softDropRight: { x: 65, y: 82 },
  hardDrop: { x: 80, y: 82 },
  right: { x: 80, y: 82 },
  ccw: { x: 84, y: 79 },
  cw: { x: 94, y: 79 },
};

export const DEFAULT_TOUCH_CONTROLS_SETTINGS: TouchControlsSettings = {
  visible: true,
  inputMode: "joystick",
  transparency: 0,
  ghostVisible: true,
  hardDropEnabled: true,
  layout: DEFAULT_TOUCH_CONTROLS_LAYOUT,
  portraitLayout: DEFAULT_TOUCH_CONTROLS_LAYOUT,
  landscapeLayout: DEFAULT_TOUCH_CONTROLS_LAYOUT,
};

const TOUCH_CONTROLS_SETTINGS_KEY = "bwwk-touch-controls-settings";
const TOUCH_CONTROLS_SETTINGS_EVENT = "bwwk-touch-controls-settings-change";

function clampTransparency(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_TOUCH_CONTROLS_SETTINGS.transparency;
  }
  return Math.min(1, Math.max(0, value));
}

function clampPosition(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(96, Math.max(4, value));
}

function normalizeLayout(value: unknown): TouchControlsLayout {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return DEFAULT_TOUCH_CONTROLS_LAYOUT;
  }

  const candidate = value as Partial<Record<TouchControlName, Partial<TouchControlPosition>>>;
  const hasDiagonalPositions = candidate.softDropLeft !== undefined || candidate.softDropRight !== undefined;
  return Object.fromEntries(
    (Object.keys(DEFAULT_TOUCH_CONTROLS_LAYOUT) as TouchControlName[]).map((name) => {
      const fallback = DEFAULT_TOUCH_CONTROLS_LAYOUT[name];
      const position = !hasDiagonalPositions && (name === "left" || name === "right")
        ? undefined
        : candidate[name];
      const migratedFallback = !hasDiagonalPositions && name === "softDropLeft"
        ? { x: candidate.left?.x ?? fallback.x, y: candidate.left?.y ?? fallback.y }
        : !hasDiagonalPositions && name === "softDropRight"
          ? { x: candidate.right?.x ?? fallback.x, y: candidate.right?.y ?? fallback.y }
          : undefined;
      const positionFallback = migratedFallback ?? fallback;
      return [name, {
        x: clampPosition(position?.x, positionFallback.x),
        y: clampPosition(position?.y, positionFallback.y),
      }];
    }),
  ) as TouchControlsLayout;
}

function isSameLayout(left: TouchControlsLayout, right: TouchControlsLayout): boolean {
  return (Object.keys(DEFAULT_TOUCH_CONTROLS_LAYOUT) as TouchControlName[]).every((name) => (
    left[name].x === right[name].x && left[name].y === right[name].y
  ));
}

export function getTouchControlsOrientation(): TouchControlsOrientation {
  if (typeof document !== "undefined") {
    return document.documentElement.dataset.orientation === "landscape" ? "landscape" : "portrait";
  }
  if (typeof window !== "undefined" && window.innerWidth >= window.innerHeight) return "landscape";
  return "portrait";
}

export function getTouchControlsLayout(
  settings: TouchControlsSettings,
  orientation: TouchControlsOrientation = getTouchControlsOrientation(),
): TouchControlsLayout {
  return orientation === "landscape" ? settings.landscapeLayout : settings.portraitLayout;
}

export function updateTouchControlsLayout(
  settings: TouchControlsSettings,
  orientation: TouchControlsOrientation,
  layout: TouchControlsLayout,
): TouchControlsSettings {
  const normalized = normalizeLayout(layout);
  return {
    ...settings,
    layout: normalized,
    portraitLayout: orientation === "portrait" ? normalized : settings.portraitLayout,
    landscapeLayout: orientation === "landscape" ? normalized : settings.landscapeLayout,
  };
}

function normalizeSettings(value: {
  visible?: unknown;
  inputMode?: unknown;
  transparency?: unknown;
  ghostVisible?: unknown;
  hardDropEnabled?: unknown;
  layout?: unknown;
  portraitLayout?: unknown;
  landscapeLayout?: unknown;
}): TouchControlsSettings {
  const storedLayout = normalizeLayout(value.layout);
  const hasOrientationLayouts = value.portraitLayout !== undefined || value.landscapeLayout !== undefined;
  const legacyLayout = !hasOrientationLayouts && isSameLayout(storedLayout, LEGACY_DEFAULT_TOUCH_CONTROLS_LAYOUT)
    ? DEFAULT_TOUCH_CONTROLS_LAYOUT
    : storedLayout;
  const portraitLayout = normalizeLayout(value.portraitLayout ?? legacyLayout);
  const landscapeLayout = normalizeLayout(value.landscapeLayout ?? legacyLayout);
  return {
    visible: value.visible !== false,
    inputMode: value.inputMode === "gesture" || value.inputMode === "buttons" || value.inputMode === "joystick"
      ? value.inputMode
      : DEFAULT_TOUCH_CONTROLS_SETTINGS.inputMode,
    transparency: clampTransparency(value.transparency),
    ghostVisible: value.ghostVisible !== false,
    hardDropEnabled: value.hardDropEnabled !== false,
    layout: portraitLayout,
    portraitLayout,
    landscapeLayout,
  };
}

export function getTouchControlsSettings(): TouchControlsSettings {
  if (typeof window === "undefined") return DEFAULT_TOUCH_CONTROLS_SETTINGS;

  try {
    const raw = window.localStorage.getItem(TOUCH_CONTROLS_SETTINGS_KEY);
    if (!raw) return DEFAULT_TOUCH_CONTROLS_SETTINGS;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_TOUCH_CONTROLS_SETTINGS;
    const candidate = parsed as {
      visible?: unknown;
      inputMode?: unknown;
      transparency?: unknown;
      ghostVisible?: unknown;
      hardDropEnabled?: unknown;
      layout?: unknown;
      portraitLayout?: unknown;
      landscapeLayout?: unknown;
    };
    return normalizeSettings(candidate);
  } catch {
    return DEFAULT_TOUCH_CONTROLS_SETTINGS;
  }
}

export function saveTouchControlsSettings(next: TouchControlsSettings): TouchControlsSettings {
  const settings = normalizeSettings(next);

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(TOUCH_CONTROLS_SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // Private browsing can reject localStorage; the setting still works this session.
    }
    window.dispatchEvent(new Event(TOUCH_CONTROLS_SETTINGS_EVENT));
  }

  return settings;
}

export function useTouchControlsSettings() {
  const [settings, setSettings] = useState<TouchControlsSettings>(getTouchControlsSettings);

  useEffect(() => {
    const refresh = () => setSettings(getTouchControlsSettings());
    window.addEventListener(TOUCH_CONTROLS_SETTINGS_EVENT, refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("resize", refresh, { passive: true });
    window.addEventListener("orientationchange", refresh, { passive: true });
    return () => {
      window.removeEventListener(TOUCH_CONTROLS_SETTINGS_EVENT, refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("orientationchange", refresh);
    };
  }, []);

  const updateSettings = useCallback((next: TouchControlsSettings) => {
    setSettings(saveTouchControlsSettings(next));
  }, []);

  return { settings, updateSettings };
}
